import { Controller } from "@hotwired/stimulus";
import { AttributeLease } from "../utils/attribute_lease";
import { FocusTrap } from "../utils/focus_trap";
import { MorphRenderWatcher } from "../utils/morph_render_watcher";

/** Reason carried by the `cancel` event: an explicit cancel vs. the Escape key. */
type CancelReason = "user" | "escape";

/**
 * Headless, accessible **alert dialog** behavior.
 *
 * Markup contract (identifier: `stimeo--alert-dialog`):
 *   <div data-controller="stimeo--alert-dialog">
 *     <button data-stimeo--alert-dialog-target="trigger"
 *             data-action="click->stimeo--alert-dialog#open">Delete…</button>
 *     <div data-stimeo--alert-dialog-target="dialog" role="alertdialog"
 *          aria-modal="true" aria-labelledby="t" aria-describedby="d" hidden>
 *       <h2 id="t">…</h2><p id="d">…</p>
 *       <button data-stimeo--alert-dialog-target="initialFocus"
 *               data-action="click->stimeo--alert-dialog#cancel">Cancel</button>
 *       <button data-action="click->stimeo--alert-dialog#confirm">Delete</button>
 *     </div>
 *   </div>
 *
 * Implements the WAI-ARIA APG **Alert Dialog** pattern. It is the same modal as
 * `stimeo--dialog` with two deliberate differences that suit an *interrupting
 * confirmation*: it never closes on a backdrop click (an accidental dismissal
 * would be dangerous), and it exposes explicit
 * {@link AlertDialogController.confirm | confirm}/{@link cancel} actions that emit
 * events so the consumer only writes the message and "what to do on confirm".
 *
 * `cancel` dispatches `{ reason: "user" | "escape" }`; `confirm` carries no detail.
 *
 * @remarks
 * Behavior only. The modal lifecycle (focus trap, scroll lock, background
 * `inert`, focus restore, teardown reversal) is delegated to the shared
 * `FocusTrap`. The only closing affordances are the confirm/cancel actions
 * and `Escape` (which cancels) — there is intentionally no backdrop close.
 *
 * Behavior provided:
 * - {@link AlertDialogController.open | open} shows the dialog and moves focus to
 *   the `initialFocus` target (the least destructive action, by convention), else
 *   the first focusable element.
 * - `Tab`/`Shift+Tab` cycle focus within the dialog (focus trap).
 * - {@link AlertDialogController.confirm | confirm} closes and dispatches
 *   `stimeo--alert-dialog:confirm`.
 * - {@link cancel} and `Escape` close and dispatch `stimeo--alert-dialog:cancel`
 *   with a `reason` of `"user"` / `"escape"`. Focus returns to the opener.
 * - A Turbo morph that puts the server's `hidden` back on an open dialog, or takes it
 *   off a closed one, is answered by writing the open state back, silently.
 */
export class AlertDialogController extends Controller<HTMLElement> {
  static override targets = ["trigger", "dialog", "initialFocus"];
  static actions = ["cancel", "confirm", "open"] as const;
  static events = ["cancel", "confirm"] as const;

  declare readonly triggerTarget: HTMLElement;
  declare readonly dialogTarget: HTMLElement;
  declare readonly dialogTargets: HTMLElement[];
  declare readonly initialFocusTargets: HTMLElement[];
  declare readonly hasTriggerTarget: boolean;
  declare readonly hasDialogTarget: boolean;

  /**
   * Owns the modal side effects. Escape takes the same cancel path as
   * {@link cancel} and emits the same event, tagged `"escape"` instead of `"user"`. Focus moves to
   * the first `initialFocus` target inside the dialog the trap is taken on, and on close returns
   * to the trigger when nothing was focused before opening.
   */
  readonly #trap = new FocusTrap(() => this.dialogTarget, {
    onEscape: () => this.#requestCancel("escape"),
    initialFocus: () => {
      const dialog = this.dialogTarget;
      return this.initialFocusTargets.find((element) => dialog.contains(element)) ?? null;
    },
    fallbackFocus: () => (this.hasTriggerTarget ? this.triggerTarget : null),
  });

  /** Whether target churn is applied: set by `connect()`, cleared first thing in `disconnect()`. */
  #connected = false;
  /** The dialog the open state was last applied to. */
  #dialog: HTMLElement | null = null;
  /** Borrows `hidden` on each dialog, to give back when one stops being the target. */
  readonly #hidden = new AttributeLease<HTMLElement>("hidden", this.identifier);
  /** Writes the open state back after a Turbo morph put the server's `hidden` in its place. */
  readonly #morphRender = new MorphRenderWatcher(() => this.#repair());

  /** Starts closed (idempotently reflects the closed state on the markup). */
  override connect(): void {
    this.#trap.connect();
    if (this.hasDialogTarget) this.#hidden.write(this.dialogTarget, "");
    this.#dialog = this.hasDialogTarget ? this.dialogTarget : null;
    this.#connected = true;
    this.#morphRender.observe(this.element);
  }

  /** Reverts the modal side effects if torn down while open (Turbo navigation). */
  override disconnect(): void {
    this.#morphRender.disconnect();
    this.#connected = false;
    this.#trap.disconnect(this);
  }

  /**
   * Shows the dialog while its trap is active and hides it otherwise, silently: what a Turbo
   * morph that put the server's `hidden` in place of the open state needs.
   */
  #repair(): void {
    if (this.hasDialogTarget) this.#hidden.write(this.dialogTarget, this.#trap.active ? null : "");
  }

  /** Applies the open state to a dialog that arrives after connect in front of the others. */
  dialogTargetConnected(): void {
    if (this.#connected) this.#adoptDialog();
  }

  /**
   * Gives a dialog that no longer resolves as the target its own `hidden` back — after
   * `disconnect()` too, since dropping the identifier leaves the element on the page — and,
   * while connected, applies the open state to the dialog left.
   */
  dialogTargetDisconnected(dialog: HTMLElement): void {
    if (this.dialogTargets.includes(dialog)) return;
    this.#hidden.return(dialog);
    if (this.#connected) this.#adoptDialog();
  }

  /** Opens the dialog, traps focus, and locks background scroll. */
  open(): void {
    if (!this.hasDialogTarget || this.#isOpen) return;
    this.#hidden.write(this.dialogTarget, null);
    this.#trap.activate();
  }

  /** Confirms: closes and dispatches `confirm`. Bound via `data-action`. */
  confirm(): void {
    if (!this.#isOpen) return;
    this.dispatch("confirm");
    this.#closeDialog();
  }

  /** Cancels (user action): closes and dispatches `cancel` with `reason: "user"`. */
  cancel(): void {
    this.#requestCancel("user");
  }

  /** Shared cancel path used by both the cancel action and the Escape key. */
  #requestCancel(reason: CancelReason): void {
    if (!this.#isOpen) return;
    this.dispatch("cancel", { detail: { reason } });
    this.#closeDialog();
  }

  /** Hides the dialog and reverts the modal side effects (restoring focus). */
  #closeDialog(): void {
    this.#hidden.write(this.dialogTarget, "");
    this.#trap.deactivate();
  }

  /**
   * Applies the open state to the dialog that is now first, when that dialog changed. An
   * open dialog moves its modal trap onto it, keeping the trap's place among the page's
   * modals and the opener focus returns to; focus moves inside unless it is already there
   * or a modal opened over this one takes `Tab`. An open dialog left with no dialog closes
   * without a decision event.
   */
  #adoptDialog(): void {
    const dialog = this.hasDialogTarget ? this.dialogTarget : null;
    if (dialog === this.#dialog) return;
    this.#dialog = dialog;
    if (!this.#trap.active) {
      if (dialog) this.#hidden.write(dialog, "");
      return;
    }
    if (!dialog) {
      this.#trap.deactivate();
      return;
    }
    this.#hidden.write(dialog, null);
    this.#trap.refreshContainer();
  }

  /** Whether the dialog is currently visible. */
  get #isOpen(): boolean {
    return this.hasDialogTarget && !this.dialogTarget.hidden;
  }
}
